/*
 * Copyright (C) 2020 - present by OpenGamma Inc. and the OpenGamma group of companies
 *
 * Please see distribution for license.
 */

import { Action } from '@ngrx/store';
import { DateTime } from 'luxon';
import {
  CalcDayRequest,
  ConfigOverride,
  FileInfo,
  StackDetails,
  UploadLocation
} from 'app/page-modules/tenant-management/modules/client-files/models';
import { CommittableData } from 'app/page-modules/tenant-management/store/models';
import { ConfigValue } from 'app/page-modules/config/models';

// -------------------------------------------------------------------------
// Clear state
export const CLEAR_STATE = '[Client Files] Clear State';

// -------------------------------------------------------------------------
// Download a client file
export const DOWNLOAD_CLIENT_FILE = '[Client Files] Download Client File';
export const DOWNLOAD_CLIENT_FILE_SUCCESS = '[Client Files] Download Client File Success';
export const DOWNLOAD_CLIENT_FILE_ERROR = '[Client Files] Download Client File Error';

// -------------------------------------------------------------------------
export const SET_SELECTED_DATE = '[Client Files] Set selected date';
export const SET_LATEST_ACTIVE_ONLY = '[Client Files] Set latest active only';
export const SET_CONSTITUENT_FILES_OWNER = '[Client Files] Set constituent files Owner';
export const HIGHLIGHT_FILES = '[Client Files] Highlight Files';

// -------------------------------------------------------------------------
// Upload a client file
export const SET_CLIENT_FILE_UPLOAD_PROGRESS =
  '[Client Files] Set Client File Upload Progress State';
export const SELECT_APPLICABLE_DATE = '[Client Files] Select Applicable Date';
export const SELECT_CLIENT_FILE_NAME = '[Client Files] Select Client File Name';
export const SELECT_CLIENT_FILE_TYPE = '[Client Files] Select Client File Type';
export const SELECT_CLIENT_FILE_FORMAT = '[Client Files] Select Client File Format';
export const SELECT_CLIENT_FILESET_GROUP = '[Client Files] Select Client FileSetGroup';
export const SELECT_CLIENT_VIEWPOINT = '[Client Files] Select Client Viewpoint';
export const SELECT_CLIENT_FILE = '[Client Files] Select Client File';
export const UPLOAD_CLIENT_FILE_REQUEST = '[Client Files] Upload Client File Request';
export const UPLOAD_CLIENT_FILE_REQUEST_SUCCESS =
  '[Client Files] Upload Client File Request Success';
export const UPLOAD_CLIENT_FILE_REQUEST_ERROR = '[Client Files] Upload Client File Request Error';
export const UPLOAD_CLIENT_FILE_UPLOAD_SUCCESS = '[Client Files] Upload Client File Upload Success';
export const UPLOAD_CLIENT_FILE_UPLOAD_ERROR = '[Client Files] Upload Client File Upload Error';
export const UPLOAD_CLIENT_FILE_FINALISE_SUCCESS =
  '[Client Files] Upload Client File Finalise Success';
export const UPLOAD_CLIENT_FILE_FINALISE_ERROR = '[Client Files] Upload Client File Finalise Error';

// -------------------------------------------------------------------------
export const SELECT_CALCULATE_ALL_OUTPUTS = '[Client Files] Select Calculate All Outputs';
export const SELECT_SCREENS = '[Client Files] Select Screens';
export const SELECT_OUTPUTS = '[Client Files] Select Outputs';
export const SELECT_SKIP_EMAIL_GENERATION = '[Client Files] Select Skip Email Generation';
export const SELECT_TEST_RUN = '[Client Files] Select Test Run';
export const SET_IS_INTERRUPTIBLE = '[Client Files] Set Is Interruptible';
export const SELECT_TEST_STACK = '[Client Files] Select Test Stack';
export const SELECT_CALC_DAY_REQUESTS = '[Client Files] Select Calc Day Requests';
export const ADD_CALC_DAY_REQUEST = '[Client Files] Add Calc Day Request';
export const CLEAR_CALCULATION_HISTORY = '[Client Files] Clear Calculation History';
export const ADD_CONFIG_OVERRIDE = '[Client Files] Add Config Override';
export const REMOVE_CONFIG_OVERRIDE = '[Client Files] Remove Config Override';

// -------------------------------------------------------------------------
// Select file filter
export const SELECT_CLIENT_FILES_TABLE_FILTER = '[Client Files] Select Client Files Table Filter';

// -------------------------------------------------------------------------
// Select tenant config type
export const SELECT_CONFIG_TYPE = '[Client Files] Select Config Type';
export const CLEAR_SELECTED_CONFIG = '[Client Files] Clear Selected Config';

// -------------------------------------------------------------------------
// Update selected config value
export const UPDATE_SELECTED_CONFIG_VALUE = '[Client Files] Update Selected Config Value';
export const SET_SELECTED_CONFIG_VALUE = '[Client Files] Set Selected Config Value';

// -------------------------------------------------------------------------
// Validate config value
export const VALIDATE_CONFIG_VALUE = '[Client Files] Validate Config Value';
export const VALIDATE_CONFIG_SUCCESS = '[Client Files] Validate Config Success';
export const VALIDATE_CONFIG_ERROR = '[Client Files] Validate Config Error';

// -------------------------------------------------------------------------
// Load tenant config value
export const LOAD_CONFIG = '[Client Files] Load Config';
export const LOAD_CONFIG_SUCCESS = '[Client Files] Load Config Success';
export const LOAD_CONFIG_ERROR = '[Client Files] Load Config Error';

// -------------------------------------------------------------------
// Clear state
export class ClearState implements Action {
  readonly type = CLEAR_STATE;
}

// -------------------------------------------------------------------------
// Download a client file
export class DownloadClientFile implements Action {
  readonly type = DOWNLOAD_CLIENT_FILE;

  constructor(public fileId: string, public reason: string) {}
}

export class DownloadClientFileSuccess implements Action {
  readonly type = DOWNLOAD_CLIENT_FILE_SUCCESS;

  constructor(public fileId: string) {}
}

export class DownloadClientFileError implements Action {
  readonly type = DOWNLOAD_CLIENT_FILE_ERROR;

  constructor(public errorMessage: string, public fileId: string) {}
}

// -------------------------------------------------------------------------
export class SetSelectedDate implements Action {
  readonly type = SET_SELECTED_DATE;

  constructor(public selectedDate: DateTime) {}
}

export class SetLatestActiveOnly implements Action {
  readonly type = SET_LATEST_ACTIVE_ONLY;

  constructor(public latestActiveOnly: boolean) {}
}

export class SetConstituentFilesOwner implements Action {
  readonly type = SET_CONSTITUENT_FILES_OWNER;

  constructor(public owner?: FileInfo) {}
}

export class HighlightFiles implements Action {
  readonly type = HIGHLIGHT_FILES;

  constructor(public fileIds: string[]) {}
}

// -------------------------------------------------------------------------
// Upload a client file
export class SetUploadFileProgressState implements Action {
  readonly type = SET_CLIENT_FILE_UPLOAD_PROGRESS;

  constructor(public inProgress: boolean) {}
}

export class SelectApplicableDate implements Action {
  readonly type = SELECT_APPLICABLE_DATE;

  constructor(public date?: DateTime) {}
}

export class SelectClientFileName implements Action {
  readonly type = SELECT_CLIENT_FILE_NAME;

  constructor(public fileName: string) {}
}

export class SelectClientFileType implements Action {
  readonly type = SELECT_CLIENT_FILE_TYPE;

  constructor(public fileType: string) {}
}

export class SelectClientFileSetGroup implements Action {
  readonly type = SELECT_CLIENT_FILESET_GROUP;

  constructor(public fileSetGroup: string | undefined) {}
}

export class SelectClientViewpoint implements Action {
  readonly type = SELECT_CLIENT_VIEWPOINT;

  constructor(public viewpoint: string) {}
}

export class SelectClientFileFormat implements Action {
  readonly type = SELECT_CLIENT_FILE_FORMAT;

  constructor(public fileFormat: string) {}
}

export class SelectClientFile implements Action {
  readonly type = SELECT_CLIENT_FILE;

  constructor(public file: File) {}
}

export class UploadClientFileRequest implements Action {
  readonly type = UPLOAD_CLIENT_FILE_REQUEST;

  constructor(public uploadReason: string) {}
}

export class UploadClientFileRequestSuccess implements Action {
  readonly type = UPLOAD_CLIENT_FILE_REQUEST_SUCCESS;

  constructor(public uploadLocation: UploadLocation, public uploadReason: string) {}
}

export class UploadClientFileRequestError implements Action {
  readonly type = UPLOAD_CLIENT_FILE_REQUEST_ERROR;

  constructor(public errorMessage: string) {}
}

export class UploadClientFileUploadSuccess implements Action {
  readonly type = UPLOAD_CLIENT_FILE_UPLOAD_SUCCESS;

  constructor(public uploadLocation: UploadLocation, public uploadReason: string) {}
}

export class UploadClientFileUploadError implements Action {
  readonly type = UPLOAD_CLIENT_FILE_UPLOAD_ERROR;

  constructor(public uploadLocation: UploadLocation, public errorMessage: string) {}
}

export class UploadClientFileFinaliseSuccess implements Action {
  readonly type = UPLOAD_CLIENT_FILE_FINALISE_SUCCESS;

  constructor() {}
}

export class UploadClientFileFinaliseError implements Action {
  readonly type = UPLOAD_CLIENT_FILE_FINALISE_ERROR;

  constructor(public uploadLocation: UploadLocation, public errorMessage: string) {}
}

// -------------------------------------------------------------------------
export class SelectCalculateAllOutputs implements Action {
  readonly type = SELECT_CALCULATE_ALL_OUTPUTS;

  constructor(public state: boolean) {}
}

export class SelectScreens implements Action {
  readonly type = SELECT_SCREENS;

  constructor(public screens: string[]) {}
}

export class SelectOutputs implements Action {
  readonly type = SELECT_OUTPUTS;

  constructor(public outputs: string[]) {}
}

export class SelectSkipEmailGeneration implements Action {
  readonly type = SELECT_SKIP_EMAIL_GENERATION;

  constructor(public state: boolean) {}
}

export class SelectTestRun implements Action {
  readonly type = SELECT_TEST_RUN;

  constructor(public state: boolean) {}
}

export class SetIsInterruptible implements Action {
  readonly type = SET_IS_INTERRUPTIBLE;

  constructor(public state: boolean) {}
}

export class SelectTestStack implements Action {
  readonly type = SELECT_TEST_STACK;

  constructor(public stack: StackDetails | undefined) {}
}

export class AddConfigOverride implements Action {
  readonly type = ADD_CONFIG_OVERRIDE;

  constructor(public configType: string, public configValue: CommittableData<string>) {}
}

export class RemoveConfigOverride implements Action {
  readonly type = REMOVE_CONFIG_OVERRIDE;

  constructor(public configOverride: ConfigOverride) {}
}

export class SelectCalcDayRequests implements Action {
  readonly type = SELECT_CALC_DAY_REQUESTS;

  constructor(public dayRequests: CalcDayRequest[]) {}
}

export class AddCalcDayRequest implements Action {
  readonly type = ADD_CALC_DAY_REQUEST;

  constructor(public dayRequest: CalcDayRequest) {}
}

export class ClearCalculationHistory implements Action {
  readonly type = CLEAR_CALCULATION_HISTORY;
}

export class SelectClientFilesTableFilter implements Action {
  readonly type = SELECT_CLIENT_FILES_TABLE_FILTER;
  constructor(public filter: string) {}
}

// -------------------------------------------------------------------------
// Select config type
export class SelectConfigType implements Action {
  readonly type = SELECT_CONFIG_TYPE;

  constructor(public configType: string) {}
}

// -------------------------------------------------------------------------
// Update selected config value
export class UpdateSelectedConfigValue implements Action {
  readonly type = UPDATE_SELECTED_CONFIG_VALUE;

  constructor(public configValue: string) {}
}

// -------------------------------------------------------------------------
// Set selected config value
export class SetSelectedConfigValue implements Action {
  readonly type = SET_SELECTED_CONFIG_VALUE;

  constructor(public configValue: CommittableData<string>) {}
}

// -------------------------------------------------------------------------
// Clear selected config type
export class ClearSelectedConfig implements Action {
  readonly type = CLEAR_SELECTED_CONFIG;

  constructor() {}
}

// -------------------------------------------------------------------------
// Validate config value
export class ValidateConfigValue implements Action {
  readonly type = VALIDATE_CONFIG_VALUE;

  constructor(public configType: string, public configValue: CommittableData<string>) {}
}

export class ValidateConfigSuccess implements Action {
  readonly type = VALIDATE_CONFIG_SUCCESS;

  constructor(public configValue: ConfigValue) {}
}

export class ValidateConfigError implements Action {
  readonly type = VALIDATE_CONFIG_ERROR;

  constructor(public errorMessage: string) {}
}

// -------------------------------------------------------------------------
// Load config
export class LoadConfig implements Action {
  readonly type = LOAD_CONFIG;

  constructor(public configType: string) {}
}

export class LoadConfigSuccess implements Action {
  readonly type = LOAD_CONFIG_SUCCESS;

  constructor(public configValue: ConfigValue) {}
}

export class LoadConfigError implements Action {
  readonly type = LOAD_CONFIG_ERROR;

  constructor(public errorMessage: string) {}
}

// -------------------------------------------------------------------------
export type All =
  | ClearState
  | DownloadClientFile
  | DownloadClientFileSuccess
  | DownloadClientFileError
  | SetSelectedDate
  | SetLatestActiveOnly
  | SetConstituentFilesOwner
  | HighlightFiles
  | SetUploadFileProgressState
  | SelectApplicableDate
  | SelectClientFileName
  | SelectClientFileType
  | SelectClientFileFormat
  | SelectClientFileSetGroup
  | SelectClientViewpoint
  | SelectClientFile
  | UploadClientFileRequest
  | UploadClientFileRequestSuccess
  | UploadClientFileRequestError
  | UploadClientFileUploadSuccess
  | UploadClientFileUploadError
  | UploadClientFileFinaliseSuccess
  | UploadClientFileFinaliseError
  | SelectCalculateAllOutputs
  | SelectScreens
  | SelectOutputs
  | SelectSkipEmailGeneration
  | SelectTestRun
  | SetIsInterruptible
  | SelectTestStack
  | SelectCalcDayRequests
  | AddConfigOverride
  | RemoveConfigOverride
  | AddCalcDayRequest
  | ClearCalculationHistory
  | SelectClientFilesTableFilter
  | SelectConfigType
  | UpdateSelectedConfigValue
  | SetSelectedConfigValue
  | ValidateConfigValue
  | ValidateConfigSuccess
  | ValidateConfigError
  | ClearSelectedConfig
  | LoadConfig
  | LoadConfigSuccess
  | LoadConfigError;
